/**
 * executionDedup — pure helpers that guard against the v7.5.14 "stale
 * In Position" bug.
 *
 * Root cause they prevent:
 *   The Electron main process re-sends today's full executions file on
 *   every `journifi-sync-watch-start` (sync toggle, app reload, etc.).
 *   Without a journal-side filter, the same ExecutionId got appended to
 *   `pendingExecutions[key]` more than once. The accumulated array fed
 *   `groupExecutionsIntoTrades`, which treated the duplicate as a real
 *   scale-in — doubling `quantity` on the trade. Because exits arrived
 *   only once, the close-detection check `totalExitQty >= totalEntryQty`
 *   permanently failed and the trade stayed `isOpen: true` even after
 *   NinjaTrader showed Position=0.
 *
 * Two-layer defense:
 *   1. filterAlreadyJournaledExecutions — drop incoming execs whose
 *      ExecutionId is already in journal trades (handles the watcher
 *      re-emit case).
 *   2. dedupExecutionsByExecutionId — dedup the per-key pending
 *      accumulator (belt-and-suspenders against same-session races).
 *
 * Lives in its own file (no imports beyond `node:` builtins) so it's
 * testable from `.mjs` test scripts without dragging in tradeProcessor's
 * bundler-only imports.
 *
 * Pure functions. No side effects.
 */

/**
 * Filter incoming executions, dropping any whose ExecutionId is already
 * present in the journal.
 *
 * @param {Array} executions   Incoming executions (from watcher push or poll)
 * @param {Set<string>|string[]} journalExecIds  ExecutionIds already in journal trades.
 *                                               Accepts a Set or array for caller convenience.
 * @returns {Array} The subset of `executions` whose ExecutionIds are NOT in the journal.
 *                  Execs without an ExecutionId (legacy/malformed) are passed
 *                  through — the downstream processor handles them as it
 *                  always has.
 */
export const filterAlreadyJournaledExecutions = (executions, journalExecIds) => {
  if (!Array.isArray(executions) || executions.length === 0) return [];
  const ids = journalExecIds instanceof Set
    ? journalExecIds
    : new Set(Array.isArray(journalExecIds) ? journalExecIds : []);
  return executions.filter(e => {
    if (!e?.ExecutionId) return true;
    return !ids.has(e.ExecutionId);
  });
};

/**
 * Dedup an execution array by ExecutionId, keeping the first occurrence.
 * Stable order (preserves input order of unique items).
 *
 * @param {Array} executions
 * @returns {Array} Same shape, duplicates removed.
 */
export const dedupExecutionsByExecutionId = (executions) => {
  if (!Array.isArray(executions) || executions.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const e of executions) {
    if (!e?.ExecutionId) {
      out.push(e);
      continue;
    }
    if (seen.has(e.ExecutionId)) continue;
    seen.add(e.ExecutionId);
    out.push(e);
  }
  return out;
};
